import React from 'react';
import axios from 'axios';
import api from '../api';
import UserStore from '@stores/userStore';
import { useAuthStore } from '@stores/auth';
import { UploadPresignedData } from '@interfaces/api.interface';
import { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import { makeVideoIdHash } from '~/utils';
import { message } from 'antd';
import i18n from '~/i18n';
const { CancelToken } = axios;

// type UploaderState = 'inactive' | 'progress' | 'complete';
export type VideoState = 'presigning' | 'uploading' | 'cancelling' | 'complete' | 'error';

export interface UploadingVideoData {
  uid: string;
  videoId: string | null;
  state: VideoState;
  title: string;
  progress: number;
  startedAt: Date;
  url: string | null;
  cancelToken?: any;
  intervalToken?: any;
}

class VideoUploader {
  public videos: { [key: string]: UploadingVideoData } = {};
  private uploadQueue: {
    uid: string;
    videoId: string;
    url: string;
    options: RcCustomRequestOptions;
  }[] = [];

  public async createVideoUpload(options: RcCustomRequestOptions, tags?: string[]) {
    const { file } = options;

    if (!(file instanceof File)) return;

    const uid = makeVideoIdHash(11);
    const title = file.name.replace(/\.[^.]*$/, '');

    this.addVideo(uid, title);

    try {
      const presigned = await this.presign(uid, title, file.name, tags);
      this.updateState(uid, 'uploading');
      this.enqueue(uid, presigned.videoId, presigned.url, options);
    } catch (error) {
      this.updateState(uid, 'error');
      if (axios.isAxiosError(error)) {
        throw new Error(error.message);
      } else {
        console.error(error);
      }
    }
  }

  public cancelVideoUpload(uid: string, silent?: boolean) {
    this.dequeue(uid, silent ?? false);
  }

  private async presign(uid: string, title: string, filename: string, tags?: string[]) {
    const t = i18n.t.bind(i18n);
    const uploadMessage = message.loading(t('header.action.upload.notification.loading'));

    window.setTimeout(uploadMessage, 3000);

    const res = await api
      .post<UploadPresignedData>(
        `/channel/${useAuthStore.getState().userData?.mainChannel}/videostream`,
        {
          title: title.substring(0, 99),
          description: '',
          type: 'explanation',
          tags,
          uploadRequest: {
            filename,
          },
        },
      )
      .catch((error) => {
        throw new Error(error);
      });

    const { url, key } = res.data;
    const videoId = key.split('/')[1];

    this.updateVideoId(uid, videoId);

    return {
      url,
      videoId,
    };
  }

  private enqueue(uid: string, videoId: string, url: string, options: RcCustomRequestOptions) {
    this.uploadQueue.push({
      uid,
      videoId,
      url,
      options,
    });

    if (this.uploadQueue.length === 1) this.upload();
  }

  private upload() {
    const { uid, videoId, url, options } = this.uploadQueue[0];
    const { file } = options;

    if (!(file instanceof File)) return;

    const contentType = file.name.endsWith('.mkv') ? 'video/x-matroska' : file.type;

    axios
      .put(url, file, {
        headers: {
          'Content-Type': contentType,
        },
        cancelToken: new CancelToken((c) => {
          this.updateCancelToken(uid, c);
        }),
        onUploadProgress: (progressEvent: ProgressEvent) => {
          function getProgress() {
            const progressPercentage = (progressEvent.loaded * 100) / progressEvent.total;

            if (progressPercentage > 0 && progressPercentage < 1) return 1;
            return Math.round(progressPercentage);
          }

          this.updateProgress(uid, getProgress());
        },
      })
      .then(() => {
        const index = this.uploadQueue.findIndex((q) => q.uid === uid);
        this.uploadQueue.splice(index, 1);
        this.updateState(uid, 'complete');
        this.updateCancelToken(uid, null);

        if (this.uploadQueue.length) this.upload();
      })
      .catch((error) => {
        if (this.videos.hasOwnProperty(uid)) this.updateState(uid, 'error');
        if (error.message !== 'Canceled by user.') {
          api.delete(`/video/${videoId}`);
        } else {
          // console.error(error);
        }
      });
  }

  private addVideo(uid: string, title: string) {
    const videoData: UploadingVideoData = {
      uid,
      videoId: null,
      state: 'presigning',
      title,
      progress: 0,
      startedAt: new Date(),
      url: null,
      cancelToken: null,
      intervalToken: null,
    };

    this.videos[uid] = videoData;
    UserStore.addUploadingVideo(videoData);
  }

  private updateState(uid: string, state: VideoState) {
    if (!this.videos.hasOwnProperty(uid)) return;

    if (state === 'complete') UserStore.completeUploadingVideo(uid);
    this.videos[uid].state = state;
    UserStore.setUploadingVideo(uid, { state });
  }

  private updateVideoId(uid: string, videoId: string) {
    if (!this.videos.hasOwnProperty(uid)) return;

    this.videos[uid].videoId = videoId;
    UserStore.setUploadingVideo(uid, { videoId });
  }

  private updateCancelToken(uid: string, cancelToken: any) {
    if (!this.videos.hasOwnProperty(uid)) return;

    this.videos[uid].cancelToken = cancelToken;
    UserStore.setUploadingVideo(uid, { cancelToken });
  }

  private updateProgress(uid: string, progress: number) {
    if (!this.videos.hasOwnProperty(uid)) return;

    this.videos[uid].progress = progress;
    UserStore.setUploadingVideo(uid, { progress });
  }

  private async dequeue(uid: string, withoutDeletion: boolean) {
    const t = i18n.t.bind(i18n);
    const videoState = this.videos[uid]?.state;

    if (!videoState) return;

    if (videoState === 'uploading') {
      const videoId = this.videos[uid].videoId;

      if (videoId) {
        this.updateState(uid, 'cancelling');
        if (!withoutDeletion) {
          await api.delete(`/video/${this.videos[uid].videoId}`).catch((error) => {
            message.error(t('common.api.error'));
            throw new Error(error);
          });
        }
      }
      if (this.videos[uid].cancelToken) {
        clearInterval(this.videos[uid].intervalToken);
        this.videos[uid].cancelToken('Canceled by user.');
      }
      message.info(t('upload.list.delete.message.title.single'));
    }

    const index = this.uploadQueue.findIndex((q) => q.uid === uid);
    this.uploadQueue.splice(index, 1);
    delete this.videos[uid];
    UserStore.removeUploadingVideo(uid);

    if (this.uploadQueue.length) this.upload();
  }

  public getUidByVideoId(videoId: string) {
    const targetVideo = Object.values(this.videos).find((v) => v.videoId === videoId);
    return targetVideo?.uid ?? null;
  }
}

export const UploaderContext = React.createContext(new VideoUploader());

export default VideoUploader;
